import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";
import AuthenticationService from "service/auth/AuthenticationService";

// ZY-10312 — ported from expert-dashboard-frontend
// (commit eb4637750fdc, src/service/realtime/CallEventStream.js). Shape kept
// intentionally identical so the two consumers of the SparkTG hub stay
// one maintenance pattern.

const MAX_BACKOFF_MS = 60000;

// A stream that has gone quiet for longer than this is treated as dead even if
// the browser still reports it OPEN. A half-dead connection behind a CDN looks
// identical to an idle one, and the server heartbeats every 25s, so silence
// this long means something in between has stopped forwarding.
const WATCHDOG_MS = 90000;

/**
 * Owns the single EventSource carrying live call events.
 *
 * Deliberately framework-free so the connection is not tied to any component's
 * lifecycle — route changes, section remounts, and in-app navigation must not
 * churn the stream (the floater is meant to be persistent).
 */
class CallEventStream {
  constructor() {
    this.apiService = new APIService();
    this.authenticationService = new AuthenticationService();

    this.eventSource = null;
    this.subscribers = new Set();
    this.refCount = 0;
    this.attempt = 0;
    this.reconnectTimer = null;
    this.watchdogTimer = null;
    this.lastMessageAt = 0;
    this.stopped = true;
  }

  /**
   * Begin streaming. Reference counted, so several mounts share one connection.
   */
  start() {
    this.refCount += 1;
    if (this.refCount > 1) return;

    this.stopped = false;
    this.attempt = 0;
    this._connect();
  }

  /**
   * Release a reference. The connection closes when the last one goes.
   */
  stop() {
    this.refCount = Math.max(0, this.refCount - 1);
    if (this.refCount > 0) return;

    this.stopped = true;
    this._teardown();
  }

  /**
   * Listen for events. Returns an unsubscribe function — always call it.
   */
  subscribe(handler) {
    this.subscribers.add(handler);
    return () => this.subscribers.delete(handler);
  }

  /**
   * Fetch the doctor's currently ongoing call.
   *
   * This is what restores the floater after a reload, so it runs on every
   * successful connect rather than only at startup.
   */
  fetchActiveCall(onResponse) {
    this.apiService.get(
      APIConfig.sparktgEvents.activeCall,
      (data) => onResponse(data && data.call ? data.call : null),
      () => onResponse(null),
    );
  }

  _emit(event) {
    this.subscribers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("call event subscriber threw", error);
      }
    });
  }

  _connect() {
    if (this.stopped) return;
    if (!this.authenticationService.isLoggedIn()) return;

    // EventSource cannot set headers, so authenticate by minting a short-lived
    // ticket over a normal (header-authenticated) request first.
    this.apiService.post(
      APIConfig.sparktgEvents.streamTicket,
      {},
      (data) => {
        if (this.stopped) return;
        if (!data || !data.ticket) {
          this._scheduleReconnect();
          return;
        }
        this._open(data.ticket);
      },
      () => this._scheduleReconnect(),
    );
  }

  _open(ticket) {
    this._closeEventSource();

    const url = `${APIConfig.sparktgEvents.stream}?ticket=${encodeURIComponent(
      ticket,
    )}`;

    let source;
    try {
      // No withCredentials: the ticket in the query string is the whole
      // authentication, and asking for credentials would additionally require
      // the ingress to echo an exact Access-Control-Allow-Origin rather than
      // the wildcard it serves today.
      source = new EventSource(url);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("failed to open call event stream", error);
      this._scheduleReconnect();
      return;
    }

    this.eventSource = source;
    this.lastMessageAt = Date.now();

    source.onopen = () => {
      this.attempt = 0;
      this.lastMessageAt = Date.now();
      this.fetchActiveCall((call) => {
        this._emit({ type: "snapshot", call });
      });
    };

    source.addEventListener("call", (message) => {
      this.lastMessageAt = Date.now();
      try {
        this._emit({ type: "call", call: JSON.parse(message.data) });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("unparseable call event", error, message.data);
      }
    });

    // Heartbeat comments do not fire a listener, but any traffic resets the
    // browser's own idea of the connection; the watchdog polls readyState.
    source.onmessage = () => {
      this.lastMessageAt = Date.now();
    };

    source.onerror = () => {
      // EventSource retries transport drops on its own, but gives up entirely
      // on an HTTP error (a rejected ticket, say) — that surfaces as CLOSED.
      // Only then do we need to re-mint and reopen.
      if (source.readyState === EventSource.CLOSED) {
        this._scheduleReconnect();
      }
    };

    this._startWatchdog();
  }

  _startWatchdog() {
    this._clearWatchdog();
    this.watchdogTimer = setInterval(() => {
      if (this.stopped) return;
      if (Date.now() - this.lastMessageAt < WATCHDOG_MS) return;

      // eslint-disable-next-line no-console
      console.warn("call event stream went silent; reconnecting");
      this._scheduleReconnect();
    }, WATCHDOG_MS / 3);
  }

  _clearWatchdog() {
    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  _scheduleReconnect() {
    if (this.stopped) return;
    if (this.reconnectTimer) return;

    this._closeEventSource();
    this._clearWatchdog();

    this.attempt += 1;
    // Exponential backoff with jitter, capped — matches the dashboard. First
    // retry fires in ~1s; a persistently failing stream caps at 60s.
    const base = Math.min(MAX_BACKOFF_MS, 1000 * (2 ** Math.max(0, this.attempt - 1)));
    const jitter = Math.floor(Math.random() * 1000);
    const delay = Math.min(MAX_BACKOFF_MS, base + jitter);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this._connect();
    }, delay);
  }

  _closeEventSource() {
    if (!this.eventSource) return;
    this.eventSource.onopen = null;
    this.eventSource.onerror = null;
    this.eventSource.onmessage = null;
    this.eventSource.close();
    this.eventSource = null;
  }

  _teardown() {
    this._closeEventSource();
    this._clearWatchdog();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

const callEventStream = new CallEventStream();

// Dev-only hook so the whole floater flow can be exercised from the console
// before the backend relay exists:
//   window.__pushCallEvent({appointmentId: 18218, state: "ongoing",
//                           callId: "t1", patientName: "Vineeta verma"})
// typeof-guarded so a non-shimmed sandbox (dynamic eval / unexpected
// bundler context) can't throw "process is not defined" at import time.
try {
  const isProd =
    typeof process !== "undefined"
    && process.env
    && process.env.NODE_ENV === "production";
  if (!isProd && typeof window !== "undefined") {
    window.__pushCallEvent = (call) =>
      callEventStream._emit({ type: "call", call });
  }
} catch (_) {
  // Dev-only hook; silently skip if the host disallows global access.
}

export default callEventStream;
